"use client";

import { m as motion, useReducedMotion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, X } from "lucide-react";

interface MvpFitContrastProps {
  isItalian: boolean;
}

const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * "Per chi e' / non e'": contrasto a due lati compatto.
 *  - Lato sinistro (ciano, +): i 4 punti "ideale per" esistenti.
 *  - Lato destro (rosso/neutro, -): anti-pattern sensati.
 * useReducedMotion -> entrambe le colonne visibili subito, niente stagger.
 */
export function MvpFitContrast({ isItalian }: MvpFitContrastProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const show = inView || reduce;

  const fit = isItalian
    ? [
        {
          title: "Hai un'idea validata",
          desc: "L'hai già confrontata con potenziali utenti e vuoi metterla alla prova sul serio.",
        },
        {
          title: "Vuoi muoverti veloce",
          desc: "Punti ad andare sul mercato in settimane, non in mesi di documenti.",
        },
        {
          title: "Preferisci qualità a quantità",
          desc: "Meglio poche funzioni fatte bene che cento abbozzate al primo rilascio.",
        },
        {
          title: "Cerchi un partner tecnico",
          desc: "Vuoi qualcuno che ragiona con te, non solo un esecutore di compiti.",
        },
      ]
    : [
        {
          title: "You have a validated idea",
          desc: "You've already tested it with potential users and want to prove it for real.",
        },
        {
          title: "You want to move fast",
          desc: "You aim to reach the market in weeks, not months of documents.",
        },
        {
          title: "You prefer quality over quantity",
          desc: "Better a few features done well than a hundred half-baked at first release.",
        },
        {
          title: "You want a technical partner",
          desc: "Someone who thinks alongside you, not just a task executor.",
        },
      ];

  const notFit = isItalian
    ? [
        {
          title: "Cerchi solo il prezzo più basso",
          desc: "Se conta solo spendere il meno possibile, a ogni costo, non siamo allineati.",
        },
        {
          title: "Vuoi cento funzioni subito",
          desc: "Tutte al primo rilascio: è l'opposto della logica di un MVP.",
        },
        {
          title: "Non hai chiaro il problema",
          desc: "Se manca ancora il problema da risolvere, conviene prima fermarsi a definirlo.",
        },
        {
          title: "Ti aspetti un prodotto finito",
          desc: "Senza confronto né iterazioni: qui si costruisce insieme, passo dopo passo.",
        },
      ]
    : [
        {
          title: "You only chase the lowest price",
          desc: "If spending the least possible at any cost is all that matters, we're not aligned.",
        },
        {
          title: "You want a hundred features now",
          desc: "All in the first release: that's the opposite of an MVP mindset.",
        },
        {
          title: "You're not clear on the problem",
          desc: "If the problem to solve is still missing, it's better to define it first.",
        },
        {
          title: "You expect a finished product",
          desc: "With no dialogue or iteration: here we build together, step by step.",
        },
      ];

  return (
    <section className="section-padding">
      <div className="container-wide" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={show ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-left mb-12 max-w-3xl"
        >
          <span className="font-mono text-label text-primary mb-3 block">
            {isItalian ? "Allineamento" : "Fit check"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian ? "Fa per te?" : "Is it for you?"}
          </h2>
          <p className="text-body-lg text-muted-foreground text-balance">
            {isItalian
              ? "Per founder e team che vanno veloci con metodo. Meglio capirsi subito."
              : "For founders and teams that move fast with method. Better to align from the start."}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5 max-w-4xl">
          {/* Lato: e' per te */}
          <motion.div
            initial={false}
            animate={show ? { opacity: 1, x: 0 } : { opacity: 0, x: reduce ? 0 : -16 }}
            transition={{ duration: reduce ? 0 : 0.5, ease: EASE }}
            className="surface-card p-5 md:p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <h3 className="font-display text-lg font-bold uppercase tracking-wide text-primary">
                {isItalian ? "È PER TE" : "IT'S FOR YOU"}
              </h3>
            </div>
            <ul className="space-y-4">
              {fit.map((item, i) => (
                <motion.li
                  key={item.title}
                  initial={false}
                  animate={
                    show ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 8 }
                  }
                  transition={{
                    duration: reduce ? 0 : 0.4,
                    delay: reduce ? 0 : 0.1 + i * 0.08,
                    ease: EASE,
                  }}
                  className="flex items-start gap-3"
                >
                  <Check className="w-4 h-4 text-primary shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <p className="text-body font-semibold leading-snug">
                      {item.title}
                    </p>
                    <p className="text-[13px] text-muted-foreground leading-relaxed mt-1">
                      {item.desc}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Lato: non fa per te */}
          <motion.div
            initial={false}
            animate={show ? { opacity: 1, x: 0 } : { opacity: 0, x: reduce ? 0 : 16 }}
            transition={{ duration: reduce ? 0 : 0.5, ease: EASE }}
            className="surface-card card-hover-red p-5 md:p-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <h3 className="font-display text-lg font-bold uppercase tracking-wide text-[hsl(var(--destructive))]">
                {isItalian ? "NON FA PER TE" : "IT'S NOT FOR YOU"}
              </h3>
            </div>
            <ul className="space-y-4">
              {notFit.map((item, i) => (
                <motion.li
                  key={item.title}
                  initial={false}
                  animate={
                    show ? { opacity: 1, y: 0 } : { opacity: 0, y: reduce ? 0 : 8 }
                  }
                  transition={{
                    duration: reduce ? 0 : 0.4,
                    delay: reduce ? 0 : 0.1 + i * 0.08,
                    ease: EASE,
                  }}
                  className="flex items-start gap-3"
                >
                  <X className="w-4 h-4 text-[hsl(var(--destructive))] shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <p className="text-body font-semibold leading-snug">
                      {item.title}
                    </p>
                    <p className="text-[13px] text-muted-foreground leading-relaxed mt-1">
                      {item.desc}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
